/**
 * components/landing/platform/sections/Hero.jsx — §3.1 Platform Hero.
 *
 * Introduce (do not sell).  A large animated visualization anchors
 * the eye while the copy positions Kalki Ai Cloud as an *operating system*,
 * not a collection of tools.
 */

import React from 'react'
import Link from '@/shims/NextLink'
import { ArrowRight, PlayCircle, Sparkles, Bot, LineChart, Wallet, ScanLine, GraduationCap, Building2, CreditCard, PieChart } from 'lucide-react'
import { Container, Eyebrow, GradientButton, GhostButton } from '@/components/landing/design-system'

const ORBIT_MODULES = [
  { i: Bot,           label: 'AI',         angle:   0, tone: 'orange' },
  { i: LineChart,     label: 'Terminal',   angle:  40, tone: 'green' },
  { i: Wallet,        label: 'Portfolio',  angle:  80, tone: 'green' },
  { i: ScanLine,      label: 'Scanner',    angle: 120, tone: 'orange' },
  { i: GraduationCap, label: 'Academy',    angle: 160, tone: 'purple' },
  { i: PieChart,      label: 'Analytics',  angle: 200, tone: 'blue' },
  { i: CreditCard,    label: 'Billing',    angle: 240, tone: 'green' },
  { i: Building2,     label: 'White-Label',angle: 280, tone: 'cyan' },
  { i: Sparkles,      label: 'Automation', angle: 320, tone: 'purple' },
]

const toneHex = { orange: '#FFC107', green: '#22c55e', blue: '#3b82f6', purple: '#a855f7', cyan: '#22d3ee' }

export default function PlatformHero() {
  const R = 180  // orbit radius
  const C = 260  // canvas centre (viewBox 520)
  return (
    <section className="relative overflow-hidden pt-28 md:pt-36 pb-16 md:pb-24">
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-[0.55]"
        style={{ background: 'radial-gradient(60% 45% at 50% 30%, rgba(255,193,7,0.16), transparent 70%)' }} />
      <div className="pointer-events-none absolute inset-0 -z-10 bg-[linear-gradient(to_bottom,rgba(0,0,0,0)_0%,rgba(0,0,0,0.4)_70%,rgba(0,0,0,1)_100%)]" />

      <Container size="wide">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          {/* copy side */}
          <div className="lg:col-span-7 flex flex-col gap-6">
            <Eyebrow>The Platform</Eyebrow>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight text-white leading-[1.02]">
              The <span className="bg-gradient-to-r from-brand-gold to-[#FFE17A] bg-clip-text text-transparent">Operating System</span> behind modern AI trading businesses.
            </h1>
            <p className="text-base md:text-lg text-zinc-400 max-w-2xl leading-relaxed">
              Kalki Ai Cloud combines AI, trading, education, business operations, automation, billing, analytics and white-label infrastructure into one enterprise platform — so you launch a business, not a stack.
            </p>
            <div className="flex flex-col sm:flex-row items-start gap-3 pt-2">
              <GradientButton href="#platform-components" size="lg">Explore the platform</GradientButton>
              <GhostButton href="/get-started" size="lg" trailingIcon>
                <PlayCircle className="h-4 w-4" /> Book a demo
              </GhostButton>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {['13 modules', '7-layer architecture', 'Multi-tenant by default', 'White-label ready'].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[11.5px] font-semibold text-zinc-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-gold" /> {t}
                </span>
              ))}
            </div>
          </div>

          {/* visualization side */}
          <div className="lg:col-span-5">
            <div className="relative aspect-square w-full max-w-[560px] mx-auto">
              <svg viewBox="0 0 520 520" className="w-full h-full">
                <defs>
                  <radialGradient id="pcore" cx="50%" cy="50%" r="60%">
                    <stop offset="0%"  stopColor="#FBF6E9" stopOpacity="0.95" />
                    <stop offset="40%" stopColor="#FFC107" stopOpacity="0.90" />
                    <stop offset="100%" stopColor="#7a2f00" stopOpacity="1" />
                  </radialGradient>
                  <radialGradient id="pglow" cx="50%" cy="50%" r="60%">
                    <stop offset="0%"  stopColor="#FFC107" stopOpacity="0.85" />
                    <stop offset="100%" stopColor="#FFC107" stopOpacity="0" />
                  </radialGradient>
                </defs>

                {/* rings */}
                <circle cx={C} cy={C} r={R - 40} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 6" fill="none" />
                <circle cx={C} cy={C} r={R}      stroke="rgba(255,255,255,0.08)" strokeDasharray="2 8" fill="none" />
                <circle cx={C} cy={C} r={R + 40} stroke="rgba(255,255,255,0.04)" fill="none" />

                {/* connection lines */}
                {ORBIT_MODULES.map((m, i) => {
                  const a = (m.angle - 90) * (Math.PI / 180)
                  const x = C + Math.cos(a) * R
                  const y = C + Math.sin(a) * R
                  return (
                    <line key={i} x1={C} y1={C} x2={x} y2={y} stroke={`rgba(255,193,7,${0.15 + (i % 3) * 0.05})`} strokeWidth="1" strokeDasharray="4 6" />
                  )
                })}

                {/* core halo */}
                <circle cx={C} cy={C} r="110" fill="url(#pglow)" opacity="0.7" className="animate-brand-pulse" style={{ transformOrigin: `${C}px ${C}px` }} />
                <circle cx={C} cy={C} r="56"  fill="url(#pcore)" />
                <text  x={C} y={C - 2}  textAnchor="middle" fontSize="15" fontWeight="800" fill="#0a0a0a" style={{ letterSpacing: '0.04em' }}>Kalki Ai Cloud</text>
                <text  x={C} y={C + 16} textAnchor="middle" fontSize="9"  fontWeight="700" fill="#0a0a0a" style={{ letterSpacing: '0.28em' }}>CORE</text>

                {/* orbiting modules */}
                {ORBIT_MODULES.map((m, i) => {
                  const a = (m.angle - 90) * (Math.PI / 180)
                  const x = C + Math.cos(a) * R
                  const y = C + Math.sin(a) * R
                  const hex = toneHex[m.tone] || '#FFC107'
                  return (
                    <g key={i} transform={`translate(${x} ${y})`}>
                      <circle r="30" fill="rgba(10,10,12,0.9)" stroke={hex} strokeOpacity="0.55" />
                      <circle r="27" fill={`${hex}12`} />
                      <foreignObject x="-14" y="-14" width="28" height="28">
                        <div className="flex items-center justify-center w-full h-full" style={{ color: hex }}>
                          <m.i strokeWidth={1.8} style={{ width: 15, height: 15 }} />
                        </div>
                      </foreignObject>
                      <text y="46" textAnchor="middle" fontSize="11" fontWeight="600" fill="rgba(255,255,255,0.85)">{m.label}</text>
                    </g>
                  )
                })}
              </svg>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}
